import React from "react"

import { useTranslation, DEFAULT_LANGUAGE, useBounceGlobalValues } from "@bounce/i18n"
import type { PostCardProps } from "@bounce/web-components"
import { CTA, PostList } from "@bounce/web-components"

import { useAppURL } from "../../../../utils/appURL"
import { Hero } from "../GuidesScreen/components/Hero"
import { Layout } from "@/components/LuggageStorage/Layout/Layout"
import type { CityData } from "@/libs/bounce/cities"
import { useCityUrl, useUrl } from "@/libs/router"
import type { Breadcrumb } from "@/types"

export type CityGuidesScreenProps = {
  city: CityData
  guides: PostCardProps[]
  layoverGuides: PostCardProps[]
  visitorGuides: PostCardProps[]
}

const CityGuidesScreen = ({ guides, layoverGuides, visitorGuides, city }: CityGuidesScreenProps) => {
  const { t } = useTranslation("marketing-generic")
  const bounceGlobals = useBounceGlobalValues()
  const { absolutePath, getUrl: getBaseUrl } = useUrl()
  const buildAppUrl = useAppURL()
  const { getUrl } = useCityUrl({ city })

  const searchUrl = buildAppUrl(getBaseUrl({ path: "/s" }), { query: city.query })

  const breadcrumbs: Breadcrumb[] = [
    {
      id: "home",
      name: t("screen.cityGuidesScreen.breadcrumbs.home", "Home"),
      href: "/",
    },
    {
      id: "guides",
      name: t("screen.cityGuidesScreen.breadcrumbs.guides", "Guides"),
      href: "/guides",
    },
    {
      id: city.slug,
      name: city.translatedName,
      href: `/guides/${city.slug}`,
    },
  ]
  const interpolations = {
    city: city.translatedName,
    numCities: bounceGlobals.numCities,
  }

  return (
    <Layout
      languages={[DEFAULT_LANGUAGE]}
      headProps={{
        title: t(
          "screen.cityGuidesScreen.metaTitleV3",
          "Travel {{city}} (and {{numCities}}+ other cities) like a local",
          interpolations,
        ),
        description: t(
          "screen.cityGuidesScreen.metaDescriptionV2",
          "Travel {{city}} like a local by reading our latest tips to maximize your trip.",
          interpolations,
        ),
        url: absolutePath,
      }}>
      <Hero
        title={t("screen.cityGuidesScreen.heroTitleV2", "Travel {{city}} like a local", interpolations)}
        subtitle={t(
          "screen.cityGuidesScreen.heroSubtitle",
          "Plan your trip with insider tips so you can experience {{city}} like a local.",
          interpolations,
        )}
        cta={{
          href: searchUrl,
          label: t("screen.cityGuidesScreen.heroCta", "Find luggage storage"),
        }}
        image={city.image}
        breadcrumbs={breadcrumbs}
      />
      {!!visitorGuides.length && (
        <PostList
          className="mt-section"
          title={t("screen.cityGuidesScreen.whatToDoV2", "Things to do in {{city}}", interpolations)}
          items={visitorGuides}
        />
      )}
      {!!layoverGuides.length && (
        <PostList
          className="mt-section"
          title={t("screen.cityGuidesScreen.layoverV2", "Make the most of a layover in {{city}}?", interpolations)}
          items={layoverGuides}
        />
      )}
      <CTA
        title={t("cmp.findLuggageStorage.titleV2", "Plan your luggage storage in {{city}}", interpolations)}
        subtitle={t(
          "cmp.findLuggageStorage.description",
          "Make the most out of your time and find luggage storage near {{city}} hotspots before checking-in into your hotel or AirBnb.",
          interpolations,
        )}
        ctaLabel={t("cmp.findLuggageStorage.cta", "Find luggage storage")}
        ctaHref={getUrl()}
        className="mt-section"
      />
      {!!guides.length && (
        <PostList
          className="mt-section"
          title={t("screen.cityGuidesScreen.guidesV2", "Explore more {{city}} travel guides", interpolations)}
          items={guides}
        />
      )}
      <div className="w-full pt-section" />
    </Layout>
  )
}

export default CityGuidesScreen
